import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Feather } from "lucide-react";
import { NAV_LINKS } from "@/data/content";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <>
      <header className="fixed inset-x-0 top-4 z-50 flex justify-center px-4">
        <nav
          data-testid="main-nav"
          className={`flex w-full max-w-5xl items-center justify-between rounded-full border border-charcoal/10 bg-cream/80 px-5 py-3 backdrop-blur-md transition-shadow duration-500 ${
            scrolled ? "shadow-[0_18px_40px_-18px_rgba(34,34,34,0.25)]" : ""
          }`}
        >
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-forest text-cream">
              <Feather size={15} />
            </span>
            <span className="font-serif text-lg font-semibold tracking-tight">The Cap Chronicles</span>
          </Link>

          <div className="hidden items-center gap-1 lg:flex">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `rounded-full px-4 py-2 text-sm font-medium transition-colors duration-300 ${
                    isActive ? "bg-forest text-cream" : "text-charcoal/70 hover:bg-charcoal/5 hover:text-charcoal"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </div>

          <button
            data-testid="nav-menu-toggle"
            onClick={() => setOpen(!open)}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-charcoal/5 lg:hidden"
            aria-label="Toggle menu"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </nav>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-x-4 top-20 z-40 rounded-3xl border border-charcoal/10 bg-cream/95 p-6 shadow-2xl backdrop-blur-lg lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-1">
              {NAV_LINKS.map((l, i) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `rounded-2xl px-4 py-3 font-serif text-2xl ${isActive ? "bg-forest/10 text-forest" : "text-charcoal"}`
                  }
                  style={{ transitionDelay: `${i * 30}ms` }}
                >
                  {l.label}
                </NavLink>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
